import React, { useState, useEffect, useMemo } from 'react';
import { UserGroupIcon, PencilIcon, TrashIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Card from '../Card';
import Table from '../Table';
import Modal from '../Modal';
import Pagination from '../Pagination';
import SearchFilter from '../SearchFilter';

const OrganizationsSection = ({ api, scenarios = [], tariffs = [], promptTemplates = [], onRefresh }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showScenariosModal, setShowScenariosModal] = useState(false);
  
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [orgScenarios, setOrgScenarios] = useState([]);
  const [availableScenarios, setAvailableScenarios] = useState([]);
  const [usersSearch, setUsersSearch] = useState('');
  const [scenariosSearch, setScenariosSearch] = useState('');
  
  const [form, setForm] = useState({ name: '', description: '', tariff_id: null, prompt_template_id: null });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.fetchOrganizations();
      setOrganizations(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const orgScenarioCount = useMemo(() => {
    const map = {};
    scenarios.forEach(s => { if (s.organization_id) map[s.organization_id] = (map[s.organization_id] || 0) + 1; });
    return map;
  }, [scenarios]);

  const filtered = useMemo(() =>
    organizations.filter(o =>
      (o.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (o.description || '').toLowerCase().includes(searchQuery.toLowerCase())
    ), [organizations, searchQuery]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleAdd = () => {
    setForm({ name: '', description: '', tariff_id: null, prompt_template_id: null });
    setShowAddModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createOrganization(form);
      toast.success('Организация создана');
      setShowAddModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (org) => {
    setCurrentOrg(org);
    setForm({ name: org.name, description: org.description, tariff_id: org.tariff_id, prompt_template_id: org.prompt_template_id || null });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateOrganization(currentOrg.id, form);
      toast.success('Организация обновлена');
      setShowEditModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (orgId) => {
    if (!window.confirm('Удалить организацию? Все связи будут удалены.')) return;
    try {
      // Unlink users and scenarios first
      const users = await api.fetchOrgUsers(orgId);
      for (const u of users) await api.removeUserFromOrg(orgId, u.id);
      const orgSc = await api.fetchOrgScenarios(orgId);
      for (const s of orgSc) await api.removeScenarioFromOrg(orgId, s.id);
      await api.deleteOrganization(orgId);
      toast.success('Организация удалена');
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openUsersModal = async (org) => {
    setCurrentOrg(org);
    setUsersSearch('');
    try {
      const [users, allUsers] = await Promise.all([
        api.fetchOrgUsers(org.id),
        api.fetchUsers()
      ]);
      setOrgUsers(users);
      const orgUserIds = new Set(users.map(u => u.id));
      setAvailableUsers(allUsers.filter(u => !orgUserIds.has(u.id)));
      setShowUsersModal(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddUser = async (userId) => {
    try {
      await api.addUserToOrg(currentOrg.id, userId);
      toast.success('Пользователь добавлен');
      const user = availableUsers.find(u => u.id === userId);
      setOrgUsers(prev => [...prev, user]);
      setAvailableUsers(prev => prev.filter(u => u.id !== userId));
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await api.removeUserFromOrg(currentOrg.id, userId);
      toast.success('Пользователь удален');
      const user = orgUsers.find(u => u.id === userId);
      setAvailableUsers(prev => [...prev, user]);
      setOrgUsers(prev => prev.filter(u => u.id !== userId));
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openScenariosModal = async (org) => {
    setCurrentOrg(org);
    setScenariosSearch('');
    try {
      const assigned = await api.fetchOrgScenarios(org.id);
      const assignedIds = new Set(assigned.map(s => s.id));
      setOrgScenarios(assigned);
      setAvailableScenarios(scenarios.filter(s => !assignedIds.has(s.id)));
      setShowScenariosModal(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAssignScenario = async (scenarioId) => {
    try {
      await api.assignScenarioToOrg(currentOrg.id, scenarioId);
      toast.success('Сценарий назначен');
      const scenario = availableScenarios.find(s => s.id === scenarioId);
      setOrgScenarios(prev => [...prev, scenario]);
      setAvailableScenarios(prev => prev.filter(s => s.id !== scenarioId));
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUnassignScenario = async (scenarioId) => {
    try {
      await api.removeScenarioFromOrg(currentOrg.id, scenarioId);
      toast.success('Сценарий удален');
      const scenario = orgScenarios.find(s => s.id === scenarioId);
      setAvailableScenarios(prev => [...prev, scenario]);
      setOrgScenarios(prev => prev.filter(s => s.id !== scenarioId));
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    { header: 'Название', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.name}</span> },
    { header: 'Описание', render: (row) => <span className="text-gray-700 dark:text-gray-300">{row.description}</span> },
    { header: 'Пользователей', className: 'hidden xs:table-cell', render: (row) => <span className="text-gray-700 dark:text-gray-300">{row.user_count || 0}</span> },
    { header: 'Сценариев', className: 'hidden xs:table-cell', render: (row) => <span className="text-gray-700 dark:text-gray-300">{orgScenarioCount[row.id] || 0}</span> },
    { header: 'Действия', render: (row) => (
      <div className="flex flex-wrap gap-1">
        <button onClick={() => openUsersModal(row)} className="bg-blue-600 text-white rounded px-2 py-1 text-xs hover:bg-blue-700 flex items-center"><UserGroupIcon className="w-3 h-3 mr-1" />Польз.</button>
        <button onClick={() => openScenariosModal(row)} className="bg-indigo-600 text-white rounded px-2 py-1 text-xs hover:bg-indigo-700 flex items-center"><ChartBarIcon className="w-3 h-3 mr-1" />Сцен.</button>
        <button onClick={() => handleEdit(row)} className="bg-yellow-600 text-white rounded px-2 py-1 text-xs hover:bg-yellow-700 flex items-center"><PencilIcon className="w-3 h-3 mr-1" />Ред.</button>
        <button onClick={() => handleDelete(row.id)} className="bg-red-600 text-white rounded px-2 py-1 text-xs hover:bg-red-700 flex items-center"><TrashIcon className="w-3 h-3 mr-1" />Удал.</button>
      </div>
    )}
  ];

  const b2bTariffs = tariffs.filter(t => t.type === 'b2b');

  return (
    <div data-section="organizations">
      <Card title="Организации" icon={UserGroupIcon} headerActions={
        <button onClick={handleAdd} className="bg-green-500 text-white rounded-lg px-3 py-1 hover:bg-green-700 text-xs sm:text-base">Добавить</button>
      }>
        <SearchFilter value={searchQuery} onChange={setSearchQuery} placeholder="Поиск по названию или описанию" />
        <Table columns={columns} data={paginated} loading={loading} error={error} emptyMessage="Нет организаций" />
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </Card>

      {/* Add Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Добавить организацию" size="md">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Название *</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full h-12 px-4 rounded-xl border dark:bg-gray-700 dark:text-gray-100" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Описание *</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full h-24 px-4 py-3 rounded-xl border dark:bg-gray-700 dark:text-gray-100 resize-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Тариф</label>
            <select value={form.tariff_id || ''} onChange={e => setForm(p => ({ ...p, tariff_id: e.target.value ? +e.target.value : null }))} className="w-full h-12 px-4 rounded-xl border dark:bg-gray-700 dark:text-gray-100">
              <option value="">Без тарифа</option>
              {b2bTariffs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Шаблон промпта</label>
            <select value={form.prompt_template_id || ''} onChange={e => setForm(p => ({ ...p, prompt_template_id: e.target.value ? +e.target.value : null }))} className="w-full h-12 px-4 rounded-xl border dark:bg-gray-700 dark:text-gray-100">
              <option value="">Без шаблона</option>
              {promptTemplates.filter(t => t.id !== '__builtin__').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">Шаблон будет использоваться для всех сценариев организации</p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 bg-gray-300 dark:bg-gray-600 text-black dark:text-gray-100 py-2 rounded-xl">Отмена</button>
            <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-xl hover:bg-green-700">Добавить</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={`Редактировать: ${currentOrg?.name}`} size="md">
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Название *</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full h-12 px-4 rounded-xl border dark:bg-gray-700 dark:text-gray-100" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Описание *</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full h-24 px-4 py-3 rounded-xl border dark:bg-gray-700 dark:text-gray-100 resize-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Тариф</label>
            <select value={form.tariff_id || ''} onChange={e => setForm(p => ({ ...p, tariff_id: e.target.value ? +e.target.value : null }))} className="w-full h-12 px-4 rounded-xl border dark:bg-gray-700 dark:text-gray-100">
              <option value="">Без тарифа</option>
              {b2bTariffs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Шаблон промпта</label>
            <select value={form.prompt_template_id || ''} onChange={e => setForm(p => ({ ...p, prompt_template_id: e.target.value ? +e.target.value : null }))} className="w-full h-12 px-4 rounded-xl border dark:bg-gray-700 dark:text-gray-100">
              <option value="">Без шаблона</option>
              {promptTemplates.filter(t => t.id !== '__builtin__').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">Шаблон будет использоваться для всех сценариев организации</p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 bg-gray-300 dark:bg-gray-600 text-black dark:text-gray-100 py-2 rounded-xl">Отмена</button>
            <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700">Сохранить</button>
          </div>
        </form>
      </Modal>

      {/* Users Modal */}
      <Modal isOpen={showUsersModal} onClose={() => setShowUsersModal(false)} title={`Пользователи: ${currentOrg?.name}`} size="5xl">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Поиск</label>
          <input type="text" value={usersSearch} onChange={e => setUsersSearch(e.target.value)} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:text-gray-100" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">В организации ({orgUsers.filter(u => (u.username || u.email || '').toLowerCase().includes(usersSearch.toLowerCase())).length})</h3>
            <div className="border rounded p-2 max-h-96 overflow-y-auto">
              {orgUsers.filter(u => (u.username || u.email || '').toLowerCase().includes(usersSearch.toLowerCase())).map(u => (
                <div key={u.id} className="flex justify-between items-center py-2 px-2 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <span className="truncate mr-2">{u.username} ({u.email})</span>
                  <button onClick={() => handleRemoveUser(u.id)} className="bg-red-600 text-white rounded px-3 py-1 text-sm hover:bg-red-700 flex-shrink-0">Удалить</button>
                </div>
              ))}
              {orgUsers.length === 0 && <div className="text-center py-3 text-gray-500">Пользователи не добавлены</div>}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Доступные ({availableUsers.filter(u => (u.username || u.email || '').toLowerCase().includes(usersSearch.toLowerCase())).length})</h3>
            <div className="border rounded p-2 max-h-96 overflow-y-auto">
              {availableUsers.filter(u => (u.username || u.email || '').toLowerCase().includes(usersSearch.toLowerCase())).map(u => (
                <div key={u.id} className="flex justify-between items-center py-2 px-2 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <span className="truncate mr-2">{u.username || u.email}</span>
                  <button onClick={() => handleAddUser(u.id)} className="bg-green-500 text-white rounded px-3 py-1 text-sm hover:bg-green-700 flex-shrink-0">Добавить</button>
                </div>
              ))}
              {availableUsers.length === 0 && <div className="text-center py-3 text-gray-500">Нет доступных пользователей</div>}
            </div>
          </div>
        </div>
      </Modal>

      {/* Scenarios Modal */}
      <Modal isOpen={showScenariosModal} onClose={() => setShowScenariosModal(false)} title={`Сценарии: ${currentOrg?.name}`} size="5xl">
        <input type="text" value={scenariosSearch} onChange={e => setScenariosSearch(e.target.value)} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:text-gray-100 mb-4" placeholder="Поиск..." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Назначенные ({orgScenarios.filter(s => s.name.toLowerCase().includes(scenariosSearch.toLowerCase())).length})</h3>
            <ul className="border rounded p-2 max-h-96 overflow-y-auto">
              {orgScenarios.filter(s => s.name.toLowerCase().includes(scenariosSearch.toLowerCase())).map(s => (
                <li key={s.id} className="flex justify-between items-center py-2 px-2 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <div className="flex-1 mr-2">
                    <div className="font-medium">{s.name}</div>
                    {s.description && <div className="text-xs text-gray-500 truncate">{s.description}</div>}
                  </div>
                  <button onClick={() => handleUnassignScenario(s.id)} className="bg-red-600 text-white rounded px-3 py-1 text-sm hover:bg-red-700 flex-shrink-0">Удалить</button>
                </li>
              ))}
              {orgScenarios.length === 0 && <li className="text-gray-400 text-center py-3">Нет назначенных сценариев</li>}
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Доступные ({availableScenarios.filter(s => s.name.toLowerCase().includes(scenariosSearch.toLowerCase())).length})</h3>
            <ul className="border rounded p-2 max-h-96 overflow-y-auto">
              {availableScenarios.filter(s => s.name.toLowerCase().includes(scenariosSearch.toLowerCase())).map(s => (
                <li key={s.id} className="flex justify-between items-center py-2 px-2 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <div className="flex-1 mr-2">
                    <div className="font-medium">{s.name}</div>
                    {s.description && <div className="text-xs text-gray-500 truncate">{s.description}</div>}
                  </div>
                  <button onClick={() => handleAssignScenario(s.id)} className="bg-green-600 text-white rounded px-3 py-1 text-sm hover:bg-green-700 flex-shrink-0">Добавить</button>
                </li>
              ))}
              {availableScenarios.length === 0 && <li className="text-gray-400 text-center py-3">Нет доступных сценариев</li>}
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationsSection;
